// Smart Sash Admin Panel - Token Manager
// Auth tokens are stored as HttpOnly cookies by the server.
// This module manages only the client-visible password-change flow state.

import Cookies from 'js-cookie';

const PASSWORD_CHANGE_REQUIRED_KEY = 'smart_sash_password_change_required';
const PASSWORD_CHANGE_FLOW_KEY = 'smart_sash_password_change_flow';

const COOKIE_ATTRS: Cookies.CookieAttributes = {
  path: '/',
  sameSite: 'strict',
  secure: import.meta.env.PROD,
};

export type PasswordChangeFlowType = 'rfid-assignment' | 'device-registration';
export type PasswordChangeNextStep = 'activating' | 'lab-selection';

export interface PasswordChangeFlowState {
  flowType: PasswordChangeFlowType;
  redirectPath: string;
  login: string;
  nextStep: PasswordChangeNextStep;
}

const isPasswordChangeFlowState = (value: unknown): value is PasswordChangeFlowState => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    (candidate.flowType === 'rfid-assignment' || candidate.flowType === 'device-registration') &&
    typeof candidate.redirectPath === 'string' &&
    typeof candidate.login === 'string' &&
    (candidate.nextStep === 'activating' || candidate.nextStep === 'lab-selection')
  );
};

export const tokenManager = {
  /**
   * Mark whether the current session must change password before normal access.
   */
  setPasswordChangeRequired: (required: boolean): void => {
    if (required) {
      Cookies.set(PASSWORD_CHANGE_REQUIRED_KEY, 'true', COOKIE_ATTRS);
      return;
    }

    Cookies.remove(PASSWORD_CHANGE_REQUIRED_KEY, { path: '/' });
  },

  /**
   * Check whether the current session requires a password change.
   */
  requiresPasswordChange: (): boolean => {
    return Cookies.get(PASSWORD_CHANGE_REQUIRED_KEY) === 'true';
  },

  /**
   * Persist the public flow that should resume after a required password change.
   */
  setPasswordChangeFlow: (flow: PasswordChangeFlowState | null): void => {
    if (!flow) {
      Cookies.remove(PASSWORD_CHANGE_FLOW_KEY, { path: '/' });
      return;
    }

    Cookies.set(PASSWORD_CHANGE_FLOW_KEY, JSON.stringify(flow), COOKIE_ATTRS);
  },

  /**
   * Read the public flow that should resume after password change.
   */
  getPasswordChangeFlow: (): PasswordChangeFlowState | null => {
    const rawValue = Cookies.get(PASSWORD_CHANGE_FLOW_KEY);
    if (!rawValue) {
      return null;
    }

    try {
      const parsedValue: unknown = JSON.parse(rawValue);
      if (isPasswordChangeFlowState(parsedValue)) {
        return parsedValue;
      }
    } catch {
      // Invalid persisted flow state should not block the user.
    }

    Cookies.remove(PASSWORD_CHANGE_FLOW_KEY, { path: '/' });
    return null;
  },

  /**
   * Store the route to resume after password change completes.
   */
  setPasswordChangeRedirect: (redirectPath: string | null): void => {
    const flow = tokenManager.getPasswordChangeFlow();

    if (redirectPath && flow) {
      tokenManager.setPasswordChangeFlow({ ...flow, redirectPath });
      return;
    }

    Cookies.remove(PASSWORD_CHANGE_FLOW_KEY, { path: '/' });
  },

  /**
   * Read the route that should resume after password change.
   */
  getPasswordChangeRedirect: (): string | null => {
    return tokenManager.getPasswordChangeFlow()?.redirectPath ?? null;
  },

  /**
   * Persist the login identifier associated with a password-change flow.
   */
  setPasswordChangeEmail: (login: string | null): void => {
    const flow = tokenManager.getPasswordChangeFlow();

    if (login && flow) {
      tokenManager.setPasswordChangeFlow({ ...flow, login });
      return;
    }

    Cookies.remove(PASSWORD_CHANGE_FLOW_KEY, { path: '/' });
  },

  /**
   * Read the login identifier associated with a password-change flow.
   */
  getPasswordChangeEmail: (): string | null => {
    return tokenManager.getPasswordChangeFlow()?.login ?? null;
  },

  /**
   * Clear the client-visible password-change flow cookies.
   * Auth tokens are HttpOnly and are cleared server-side on logout.
   */
  clearTokens: (): void => {
    Cookies.remove(PASSWORD_CHANGE_REQUIRED_KEY, { path: '/' });
    Cookies.remove(PASSWORD_CHANGE_FLOW_KEY, { path: '/' });
  },
};

export default tokenManager;
